import { formatMoney, t } from "@hakili/i18n";
import { BadgeCheck, PhoneCall } from "lucide-react";
import { useState } from "react";

/**
 * Round 10a (`OD-37`, workflow §1.5, WF-CON-13, decision D-1010-3): **« Vérification d'emploi » on the dossier , the
 * chargée's own act.** She calls or e-mails the employer at the contact printed on the attestation, asks the open
 * questions without supplying the answers, and records what she was told. The door appends the act with her name from
 * her session; this form sends no author. The act is a condition of « Soumettre aux banques », with no bypass: the
 * stage panel greys the send with the door's own sentence until an act stands on the dossier.
 *
 * Nothing here decides: the act and the gate's state are the door's, and a refusal is printed in the door's words.
 */
export interface VerificationView {
  readonly id: string;
  readonly method: "appel" | "courriel";
  readonly contactUsed: string;
  readonly contactSource: string;
  readonly contactedAt: string;
  readonly personReached: string;
  readonly personTitle: string;
  readonly answers: {
    readonly worksHere: boolean;
    readonly since: string | null;
    readonly position: string | null;
    readonly salaryStated: number | null;
  };
  readonly observations: string | null;
  readonly byName: string;
  readonly recordedAt: string;
}

/** `GET /dossiers/{id}/employment-verification`: the act that rules, and the send gate's state. */
export interface EmploymentVerificationState {
  readonly verification: VerificationView | null;
  readonly gate: { readonly open: boolean; readonly reason: string | null };
}

/** The body the door takes (`POST /dossiers/{id}/employment-verification`): no author. */
export interface VerificationBody {
  readonly method: "appel" | "courriel";
  readonly contactUsed: string;
  readonly contactSource: "attestation";
  readonly contactedAt: string;
  readonly personReached: string;
  readonly personTitle: string;
  readonly answers:
    | { readonly worksHere: false }
    | {
        readonly worksHere: true;
        readonly since: string;
        readonly position: string;
        readonly salaryStated: number;
      };
  readonly observations?: string;
}

const dayFrench = (iso: string): string => {
  const [year, month, day] = iso.slice(0, 10).split("-");
  return `${day ?? ""}/${month ?? ""}/${year ?? ""}`;
};
const francs = (amount: number): string => formatMoney(Math.trunc(amount));

const failureWords = (failure: unknown): string =>
  (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee");

const INPUT =
  "w-full rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-indigo-200";
const LABEL = "text-xs font-medium text-gray-700";

export function EmploymentVerificationSection(props: {
  readonly state: EmploymentVerificationState | null;
  /** The chargée may act: her file, an open stage. */
  readonly canAct: boolean;
  readonly busy: boolean;
  readonly onRecord: (body: VerificationBody) => Promise<void>;
}): React.JSX.Element {
  const { state } = props;
  const done = state?.verification ?? null;
  const [adding, setAdding] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [method, setMethod] = useState<"appel" | "courriel">("appel");
  const [contact, setContact] = useState("");
  const [at, setAt] = useState("");
  const [person, setPerson] = useState("");
  const [title, setTitle] = useState("");
  const [works, setWorks] = useState<boolean | null>(null);
  const [since, setSince] = useState("");
  const [position, setPosition] = useState("");
  const [salary, setSalary] = useState("");
  const [observations, setObservations] = useState("");
  const [sending, setSending] = useState(false);

  const showForm = props.canAct && (done === null || adding);

  const submit = (): void => {
    void (async () => {
      setSending(true);
      setRefusal(null);
      const asked = at === "" ? "" : new Date(at);
      const body = {
        method,
        contactUsed: contact,
        contactSource: "attestation",
        contactedAt: asked === "" || Number.isNaN(asked.getTime()) ? at : asked.toISOString(),
        personReached: person,
        personTitle: title,
        answers:
          works === true
            ? { worksHere: true, since, position, salaryStated: salary === "" ? salary : Number(salary) }
            : works === false
              ? { worksHere: false }
              : {},
        ...(observations.trim() === "" ? {} : { observations: observations.trim() }),
      };
      try {
        // The door checks the body and answers in French; this form sends what she typed and prints the answer.
        await props.onRecord(body as unknown as VerificationBody);
        setAdding(false);
      } catch (failure) {
        setRefusal(failureWords(failure));
      } finally {
        setSending(false);
      }
    })();
  };

  return (
    <section
      data-testid="verification-emploi"
      className="rounded-2xl border border-gray-200 bg-white px-5 py-4"
    >
      <div className="mb-2 flex items-center gap-2">
        <PhoneCall className="h-4 w-4 text-indigo-600" aria-hidden="true" />
        <h3 className="text-sm font-semibold">{t("console.verification.titre")}</h3>
      </div>

      {done === null ? (
        <p className="text-sm text-gray-500" data-testid="verification-emploi-absente">
          {t("console.verification.absente")}
        </p>
      ) : (
        <div data-testid="verification-emploi-fait" className="flex flex-col gap-1">
          <p className="flex items-start gap-2 text-sm text-gray-900">
            <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
            <span>
              {t("console.verification.faite")
                .replace("{nom}", done.byName)
                .replace(
                  "{methode}",
                  t(
                    done.method === "appel"
                      ? "console.verification.methodeAppel"
                      : "console.verification.methodeCourriel",
                  ).toLowerCase(),
                )
                .replace("{personne}", done.personReached)
                .replace("{fonction}", done.personTitle)
                .replace("{date}", dayFrench(done.contactedAt))}
            </span>
          </p>
          <p className="text-sm text-gray-500">
            {t("console.verification.reponsesFaites").replace(
              "{reponses}",
              done.answers.worksHere
                ? t("console.verification.travailleDepuis")
                    .replace("{depuis}", done.answers.since ?? "")
                    .replace("{poste}", done.answers.position ?? "")
                    .replace(
                      "{salaire}",
                      done.answers.salaryStated === null ? "" : francs(done.answers.salaryStated),
                    )
                : t("console.verification.nonTravaille"),
            )}
          </p>
        </div>
      )}

      {state !== null && !state.gate.open ? (
        <p className="mt-2 text-sm text-amber-700" data-testid="verification-garde">
          {t("console.verification.soumissionAttend").replace("{motif}", state.gate.reason ?? "")}
        </p>
      ) : null}

      {props.canAct && done !== null && !adding ? (
        <button
          type="button"
          data-testid="verification-nouvelle"
          onClick={() => {
            setAdding(true);
          }}
          className="mt-3 w-fit rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          {t("console.verification.nouvelle")}
        </button>
      ) : null}

      {showForm ? (
        <form
          className="mt-3 flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <p className="text-sm text-gray-500">{t("console.verification.aide")}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1">
              <span className={LABEL}>{t("console.verification.methode")}</span>
              <select
                data-testid="verification-methode"
                value={method}
                onChange={(event) => {
                  setMethod(event.target.value === "courriel" ? "courriel" : "appel");
                }}
                className={INPUT}
              >
                <option value="appel">{t("console.verification.methodeAppel")}</option>
                <option value="courriel">{t("console.verification.methodeCourriel")}</option>
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className={LABEL}>{t("console.verification.date")}</span>
              <input
                type="datetime-local"
                data-testid="verification-date"
                value={at}
                onChange={(event) => {
                  setAt(event.target.value);
                }}
                className={INPUT}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className={LABEL}>{t("console.verification.contact")}</span>
              <input
                data-testid="verification-contact"
                value={contact}
                onChange={(event) => {
                  setContact(event.target.value);
                }}
                className={INPUT}
              />
            </label>
            <div className="flex flex-col gap-1">
              <span className={LABEL}>{t("console.verification.contactObtenuPar")}</span>
              <p className="py-1.5 text-sm text-gray-900" data-testid="verification-source">
                {t("console.verification.contactAttestation")}
              </p>
            </div>
            <label className="flex flex-col gap-1">
              <span className={LABEL}>{t("console.verification.personne")}</span>
              <input
                data-testid="verification-personne"
                value={person}
                onChange={(event) => {
                  setPerson(event.target.value);
                }}
                className={INPUT}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className={LABEL}>{t("console.verification.fonction")}</span>
              <input
                data-testid="verification-fonction"
                value={title}
                onChange={(event) => {
                  setTitle(event.target.value);
                }}
                className={INPUT}
              />
            </label>
          </div>

          <fieldset className="flex flex-col gap-2">
            <legend className={LABEL}>{t("console.verification.reponses")}</legend>
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-900">
              <span className="text-xs text-gray-700">{t("console.verification.travaille")}</span>
              <label className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name="verification-travaille"
                  data-testid="verification-travaille-oui"
                  checked={works === true}
                  onChange={() => {
                    setWorks(true);
                  }}
                  onClick={() => {
                    setWorks(true);
                  }}
                />
                {t("console.verification.oui")}
              </label>
              <label className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name="verification-travaille"
                  data-testid="verification-travaille-non"
                  checked={works === false}
                  onChange={() => {
                    setWorks(false);
                  }}
                  onClick={() => {
                    setWorks(false);
                  }}
                />
                {t("console.verification.non")}
              </label>
            </div>
            {works === true ? (
              <div className="grid gap-3 sm:grid-cols-3">
                <label className="flex flex-col gap-1">
                  <span className={LABEL}>{t("console.verification.depuis")}</span>
                  <input
                    type="month"
                    data-testid="verification-depuis"
                    value={since}
                    onChange={(event) => {
                      setSince(event.target.value);
                    }}
                    className={INPUT}
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className={LABEL}>{t("console.verification.poste")}</span>
                  <input
                    data-testid="verification-poste"
                    value={position}
                    onChange={(event) => {
                      setPosition(event.target.value);
                    }}
                    className={INPUT}
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className={LABEL}>{t("console.verification.salaire")}</span>
                  <input
                    type="number"
                    min={0}
                    data-testid="verification-salaire"
                    value={salary}
                    onChange={(event) => {
                      setSalary(event.target.value);
                    }}
                    className={INPUT}
                  />
                </label>
              </div>
            ) : null}
          </fieldset>

          <label className="flex flex-col gap-1">
            <span className={LABEL}>{t("console.verification.observations")}</span>
            <textarea
              rows={2}
              data-testid="verification-observations"
              value={observations}
              onChange={(event) => {
                setObservations(event.target.value);
              }}
              className={INPUT}
            />
          </label>

          {refusal === null ? null : (
            <p role="alert" data-testid="verification-refus" className="text-xs text-rose-600">
              {refusal}
            </p>
          )}
          <div className="flex gap-2">
            <button
              type="submit"
              data-testid="verification-enregistrer"
              disabled={props.busy || sending}
              className="rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 px-3.5 py-2 text-sm font-medium disabled:opacity-40"
            >
              {t("console.verification.enregistrer")}
            </button>
            {done !== null ? (
              <button
                type="button"
                data-testid="verification-annuler"
                onClick={() => {
                  setAdding(false);
                  setRefusal(null);
                }}
                className="rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                {t("console.verification.annuler")}
              </button>
            ) : null}
          </div>
        </form>
      ) : null}
    </section>
  );
}
